/**
 * Server state for build step 5 — usage upload, utilisation, forecast and alerts. Every read and write goes
 * through a contract route (packages/contracts routes.step5); the SPA never recomputes utilisation, the
 * forecast or an alert rule — it renders what GET /usage/* returns (ADR §8.3, §10.1).
 *
 * The report upload is the four-step flow of ADR §10.1: sign a slot → PUT the bytes straight to storage →
 * parse (in the worker) → poll the staged rows → apply (or discard).
 */
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  Alert,
  AlertRule,
  ApplyUploadBody,
  ClientProductUsage,
  ClientUsage,
  CreateAliasBody,
  CreateClientProductBody,
  CreateProductBody,
  ManualUsageBody,
  Product,
  SignUploadBody,
  SignUploadResponse,
  UploadPurpose,
  UpdateClientProductBody,
  UpdateProductBody,
  UploadDetail,
  UsageTone,
} from '@amplispot/contracts';
import { buildPath, MAX_CLIENT_PRODUCTS_BATCH, MAX_CLIENT_USAGE_BATCH, routes } from '@amplispot/contracts';
import { call, newIdempotencyKey } from '../api';

export const ukeys = {
  all: ['usage'] as const,
  products: ['usage', 'products'] as const,
  aliases: (key: string) => ['usage', 'aliases', key] as const,
  overview: ['usage', 'overview'] as const,
  client: (id: string) => ['usage', 'client', id] as const,
  /** GET /usage/clients: 'active', or the sorted ids joined. Under `usage`, so refreshUsage reloads it after a write. */
  clientsBatch: (which: string) => ['usage', 'clients-batch', which] as const,
  alerts: ['usage', 'alerts'] as const,
  uploads: ['usage', 'uploads'] as const,
  upload: (id: string) => ['usage', 'upload', id] as const,
  clientProducts: (id: string) => ['usage', 'client-products', id] as const,
  /** GET /client-products: 'active', or the sorted ids joined. Under `usage`, so refreshUsage reloads it after a write. */
  clientProductsBatch: (which: string) => ['usage', 'client-products-batch', which] as const,
};

/** A usage write changes the grid, alerts, the client page, the upload list, health (clients) and /me badges. */
export function refreshUsage(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: ukeys.all });
  void qc.invalidateQueries({ queryKey: ['clients'] });
  void qc.invalidateQueries({ queryKey: ['me'] });
}

type Paged<T> = { data: T[]; next_cursor: string | null };
async function allPages<T>(fetchPage: (cursor: string | undefined) => Promise<Paged<T>>): Promise<T[]> {
  const out: T[] = [];
  let cursor: string | undefined;
  do {
    const p = await fetchPage(cursor);
    out.push(...p.data);
    cursor = p.next_cursor ?? undefined;
  } while (cursor);
  return out;
}

// ---------------------------------------------------------------- formatting

/** Prototype `nf`: Indian digit grouping, "-" for nothing. */
export const nf = (n: number | null | undefined): string => (n == null ? '-' : Math.round(n).toLocaleString('en-IN'));
/** Prototype `arrow`. */
export const arrow = (t: number | null | undefined): string => (t == null || t === 0 ? '' : t > 0 ? '▲' : '▼');
export const signed = (t: number | null | undefined): string => (t == null ? '-' : `${t > 0 ? '+' : ''}${Math.round(t)}%`);

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "2026-09" → "Sep 2026"; the current month reads "Oct 2026 (so far)" (prototype UPERIODS). */
export function periodLabel(period: string, current?: string): string {
  const [y, m] = period.split('-');
  const s = `${MON[Number(m) - 1] ?? m} ${y}`;
  return current && period === current ? `${s} (so far)` : s;
}
export const monthShort = (period: string): string => MON[Number(period.split('-')[1]) - 1] ?? period;

/** This month in India time, as YYYY-MM. */
export function currentPeriod(): string {
  const d = new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', timeZone: 'Asia/Kolkata' }).format(new Date());
  return d.slice(0, 7);
}
/** The report periods offered: this month and the two before it (prototype UPERIODS). */
export function reportPeriods(n = 3): string[] {
  const [y, m] = currentPeriod().split('-').map(Number) as [number, number];
  const out: string[] = [];
  for (let i = 0; i < n; i++) {
    const d = new Date(Date.UTC(y, m - 1 - i, 1));
    out.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}

/** "Mon 5 Oct" in India time (prototype log dates). */
export function shortDay(ts: string): string {
  return new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' })
    .format(new Date(ts))
    .replace(',', '');
}

/**
 * The forecast to show, or `null`. ADR §8.3: a fit under Settings → forecast.min_r2 (default 0.3) is noise, so
 * no line, bar, tile, days-to-low-line or predicted "!" is drawn from it — the UI says FORECAST_UNEVEN instead.
 */
export const usableForecast = (f: ClientProductUsage['forecast']) => (f && f.value.meaningful ? f.value : null);
/** True when there is a forecast but its fit is too weak to show (as opposed to no forecast at all). */
export const forecastTooUneven = (f: ClientProductUsage['forecast']) => !!f && !f.value.meaningful;
export const FORECAST_UNEVEN = 'Too uneven to forecast';

/** Prototype uChip for a value against the product's lines. */
export const toneOf = (util: number | null, low: number, watch: number): UsageTone =>
  util == null ? 'info' : util < low ? 'cold' : util < watch ? 'warm' : 'hot';

// ---------------------------------------------------------------- reads

export function useProducts() {
  return useQuery({
    queryKey: ukeys.products,
    queryFn: async (): Promise<Product[]> => (await call('products.list')).data,
  });
}

export function useAliases(key: string | null, enabled = true) {
  return useQuery({
    queryKey: ukeys.aliases(key ?? ''),
    queryFn: async () => (await call('aliases.list', { params: { key: key! } })).data,
    enabled: !!key && enabled,
  });
}

export function useUsageOverview() {
  return useQuery({ queryKey: ukeys.overview, queryFn: () => call('usage.overview', { query: {} }) });
}

export function useClientUsage(id: string | null) {
  return useQuery({
    queryKey: ukeys.client(id ?? ''),
    queryFn: () => call('usage.client', { params: { id: id! }, query: {} }),
    enabled: !!id,
  });
}

/**
 * Many clients' usage pages through GET /usage/clients (t-b083972d), keyed by client id. Each is exactly what
 * GET /usage/clients/{id} returns. `'active'` is every active client the caller can see, in ONE request; a list of ids
 * is one request per 200 ids (usually one); no ids, none. It used to be one GET /usage/clients/{id} per client: ~230
 * requests a Money › Usage load, 429s on the third reload in a minute (600/min per session).
 */
export async function fetchClientUsages(ids: readonly string[] | 'active'): Promise<Map<string, ClientUsage>> {
  const out = new Map<string, ClientUsage>();
  const chunks: (string[] | null)[] = [];
  if (ids === 'active') chunks.push(null);
  else {
    const want = [...new Set(ids)].sort();
    for (let i = 0; i < want.length; i += MAX_CLIENT_USAGE_BATCH) chunks.push(want.slice(i, i + MAX_CLIENT_USAGE_BATCH));
  }
  for (const c of chunks) {
    const r = await call('usage.clients', { query: c ? { client_ids: c.join(',') } : {} });
    for (const u of r.data) out.set(u.client.id, u);
  }
  return out;
}

/** The query behind useClientUsages (exported so a test can run exactly what the hook runs). */
export function clientUsagesQuery(ids: readonly string[], { allActive = false }: { allActive?: boolean } = {}) {
  return {
    queryKey: ukeys.clientsBatch(allActive ? 'active' : [...new Set(ids)].sort().join(',')),
    queryFn: () => fetchClientUsages(allActive ? 'active' : ids),
    enabled: ids.length > 0,
  };
}

/**
 * Several clients' usage pages at once (the alert cards need each one's 14-day line and tip; Money › Usage each
 * client's source and extras). Per id: `data`, whether it `failed` (the read failed, or came back without that client,
 * so a card can fall back instead of showing a loading mark forever) and `retry`.
 *
 * `allActive`: `ids` are every active client the caller can see (GET /usage/overview's clients), so it is read in
 * one request whatever their number; otherwise one request per 200 ids.
 */
export function useClientUsages(ids: readonly string[], opts: { allActive?: boolean } = {}) {
  const q = useQuery(clientUsagesQuery(ids, opts));
  return useMemo(() => {
    const settled = q.isError || q.isSuccess;
    const retry = () => void q.refetch();
    const m = new Map<string, { data: ClientUsage | undefined; failed: boolean; retry: () => void }>();
    for (const id of ids) {
      const data = q.data?.get(id);
      m.set(id, { data, failed: settled && !data, retry });
    }
    return m;
  }, [ids, q.data, q.isError, q.isSuccess, q.refetch]);
}

/** Every alert that is still live (open or acknowledged), every page. */
export function useAlerts() {
  return useQuery({
    queryKey: ukeys.alerts,
    queryFn: async (): Promise<Alert[]> => {
      const [open, acked] = await Promise.all(
        (['open', 'acknowledged'] as const).map((state) =>
          allPages((cursor) => call('usage.alerts', { query: { state, limit: 200, cursor } })),
        ),
      );
      return [...open!, ...acked!];
    },
  });
}

export function useUploads() {
  return useQuery({ queryKey: ukeys.uploads, queryFn: () => call('uploads.list', { query: { limit: 200 } }) });
}

/** Reports → "Reports entered": the newest entries (GET /usage/log, already scoped by role). */
export function useUsageLog(limit = 12) {
  return useQuery({ queryKey: [...ukeys.all, 'log', limit] as const, queryFn: () => call('usage.log', { query: { limit } }) });
}

/** GET /usage-report-template/{key}.xlsx — a same-origin link, so the session cookie goes with it. */
export const templateHref = (product_key: string) => buildPath(routes['usage.template'].path, { product_key });

export function useClientProducts(id: string | null) {
  return useQuery({
    queryKey: ukeys.clientProducts(id ?? ''),
    queryFn: async () => (await call('clientProducts.list', { params: { id: id! } })).data,
    enabled: !!id,
  });
}

type ClientProductRow = Awaited<ReturnType<typeof call<'clientProducts.batch'>>>['data'][number]['products'][number];

/**
 * Many clients' products through GET /client-products (t-c1371dd8), keyed by client id. `'active'` is every active
 * client the caller can see, in ONE request; a list of ids is one request per 200 ids (usually one); no ids, none.
 * It used to be one GET /clients/{id}/products per client: 230 requests a load, 429s on the third reload in a minute.
 */
export async function fetchClientProducts(ids: readonly string[] | 'active'): Promise<Map<string, ClientProductRow[]>> {
  const out = new Map<string, ClientProductRow[]>();
  const chunks: (string[] | null)[] = [];
  if (ids === 'active') chunks.push(null);
  else {
    const want = [...new Set(ids)].sort();
    for (let i = 0; i < want.length; i += MAX_CLIENT_PRODUCTS_BATCH) chunks.push(want.slice(i, i + MAX_CLIENT_PRODUCTS_BATCH));
  }
  for (const c of chunks) {
    const r = await call('clientProducts.batch', { query: c ? { client_ids: c.join(',') } : {} });
    for (const g of r.data) out.set(g.client_id, g.products);
  }
  return out;
}

const NO_PRODUCTS = new Map<string, ClientProductRow[]>();

/**
 * clientProducts for many clients (Settings → What each client bought: `'active'`; Settings › Integrations: the
 * matched clients' ids). `productsOf(id)` is undefined while loading, then that client's products. Once the read has
 * settled, a client it left out (created after it was cached, or one the caller cannot see) is an empty list, not
 * a spinner that never ends (t-8657b79a).
 */
export function useManyClientProducts(ids: readonly string[] | 'active') {
  const which = ids === 'active' ? 'active' : [...new Set(ids)].sort().join(',');
  const q = useQuery({
    queryKey: ukeys.clientProductsBatch(which),
    queryFn: () => fetchClientProducts(ids),
    enabled: ids === 'active' || ids.length > 0,
  });
  const byClient = q.data ?? NO_PRODUCTS;
  return { byClient, productsOf: productsLookup(byClient, q.isSuccess), loading: q.isLoading, error: q.error ?? null };
}

/** A client's products from a batch read: undefined until it has settled, then a client it left out is []. */
export const productsLookup = (byClient: ReadonlyMap<string, ClientProductRow[]>, settled: boolean) =>
  (id: string): ClientProductRow[] | undefined => byClient.get(id) ?? (settled ? [] : undefined);

// ---------------------------------------------------------------- alerts, grouped as the prototype shows them

/** The order the prototype writes the reasons in (uAlerts). */
const RULE_ORDER: AlertRule[] = [
  'down_15_vs_last_month',
  'util_below_low_line',
  'forecast_below_low_line',
  'forecast_below_watch_falling',
  'four_months_decline',
  'no_data_reported',
];

/** One card per client-product: the rules that fired, joined into one plain-English why (prototype uAlerts). */
export interface AlertGroup {
  key: string;
  client: Alert['client'];
  product_key: string;
  product_name: string;
  /** cold = at risk, warm = watch, info = no data. */
  sev: 'cold' | 'warm' | 'info';
  predicted: boolean;
  why: string;
  score: number;
  alerts: Alert[];
}

const sentence = (s: string) => s.trim().replace(/\.+$/, '');

export function groupAlerts(alerts: Alert[]): AlertGroup[] {
  const m = new Map<string, Alert[]>();
  for (const a of alerts) {
    const k = `${a.client.id}|${a.product_key}`;
    m.set(k, [...(m.get(k) ?? []), a]);
  }
  const out: AlertGroup[] = [];
  for (const [key, list] of m) {
    const real = list.filter((a) => a.severity !== 'info');
    const use = (real.length ? real : list).sort((a, b) => RULE_ORDER.indexOf(a.rule) - RULE_ORDER.indexOf(b.rule));
    const first = use[0]!;
    out.push({
      key,
      client: first.client,
      product_key: first.product_key,
      product_name: first.product_name,
      sev: !real.length ? 'info' : real.some((a) => a.severity === 'at_risk') ? 'cold' : 'warm',
      predicted: real.some((a) => a.rule.startsWith('forecast_')),
      why: `${use.map((a) => sentence(a.why)).join('. ')}.`,
      score: Math.max(...use.map((a) => a.score)),
      alerts: use,
    });
  }
  return out.sort((a, b) => b.score - a.score);
}

/** Client-products the forecast says will cross the low-use line (the red ! on the heat grid). */
export const predictedLow = (alerts: Alert[]): Set<string> =>
  new Set(alerts.filter((a) => a.rule === 'forecast_below_low_line').map((a) => `${a.client.id}|${a.product_key}`));

// ---------------------------------------------------------------- writes

export function useManualUsage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ManualUsageBody) => call('usage.manual', { body, idempotencyKey: newIdempotencyKey() }),
    onSuccess: () => refreshUsage(qc),
  });
}

/**
 * Sign a slot and send the file straight to storage. Returns the upload id and the storage key (the key is
 * what a typed-in entry attaches as `file_key`).
 */
/**
 * Sign, then upload, one file. `purpose` 'report' (default: the Upload modal, then parsed) or 'attachment' (the
 * Type-in modal's proof, sent on as ManualUsageBody.file_key; never parsed, so never call parseAndWait on it).
 */
export async function sendFile(f: File, product_key: string, period: string, purpose: UploadPurpose = 'report'): Promise<{ upload_id: string; spaces_key: string }> {
  const body: SignUploadBody = { product_key, period, purpose, filename: f.name, bytes: f.size, content_type: f.type || 'application/octet-stream' };
  const slot = await call('uploads.sign', { body, idempotencyKey: newIdempotencyKey() });
  await uploadToSlot(slot, f, body.content_type);
  return { upload_id: slot.upload_id, spaces_key: slot.spaces_key };
}

/**
 * Send a file to a signed slot (any importer's sign answer: usage reports, history sheets). Throws one plain
 * sentence on any failure, never "Failed to fetch".
 */
export async function uploadToSlot(slot: Pick<SignUploadResponse, 'url' | 'method' | 'fields'>, f: File, contentType: string): Promise<void> {
  const failed = new Error('The file did not upload. Check your connection and try again.');
  let res: Response;
  // A network or CORS failure rejects the fetch itself; it must read the same as a refused upload, never "Failed to fetch".
  try {
    if (slot.method === 'PUT') {
      // The signed fields already carry Content-Type (it is part of the signature). Adding our own 'content-type' as
      // a second key made fetch send "text/csv, text/csv": the local store answered 415 and Spaces would refuse the
      // signature. Header names are case-insensitive, so set it only when the slot did not.
      const headers = new Headers(slot.fields);
      if (!headers.has('content-type')) headers.set('content-type', contentType);
      res = await fetch(slot.url, { method: 'PUT', headers, body: f });
    } else {
      const fd = new FormData();
      for (const [k, v] of Object.entries(slot.fields)) fd.append(k, v);
      fd.append('file', f);
      res = await fetch(slot.url, { method: 'POST', body: fd });
    }
  } catch {
    throw failed;
  }
  if (!res.ok) throw failed;
}

/** Parse in the worker and poll until the rows are staged (or the parse fails). */
export async function parseAndWait(id: string, signal?: AbortSignal): Promise<UploadDetail> {
  await call('uploads.parse', { params: { id } });
  for (let i = 0; i < 120; i++) {
    if (signal?.aborted) throw new DOMException('aborted', 'AbortError');
    const d = await call('uploads.get', { params: { id } });
    if (d.upload.status !== 'parsing' && d.upload.status !== 'awaiting_file') return d;
    await new Promise((r) => setTimeout(r, i < 10 ? 500 : 1500));
  }
  throw new Error('Reading the file is taking longer than usual. Try again in a minute.');
}

export function useApplyUpload() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; body: ApplyUploadBody }) => call('uploads.apply', { params: { id: v.id }, body: v.body }),
    onSuccess: () => refreshUsage(qc),
  });
}

export const discardUpload = (id: string) => call('uploads.discard', { params: { id } });

export function useCreateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateProductBody) => call('products.create', { body, idempotencyKey: newIdempotencyKey() }),
    onSuccess: () => refreshUsage(qc),
  });
}
export function useUpdateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { key: string; body: UpdateProductBody }) => call('products.update', { params: { key: v.key }, body: v.body }),
    onSuccess: () => refreshUsage(qc),
  });
}
export function useCreateAlias() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { key: string; body: CreateAliasBody }) =>
      call('aliases.create', { params: { key: v.key }, body: v.body, idempotencyKey: newIdempotencyKey() }),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: ukeys.aliases(v.key) });
      void qc.invalidateQueries({ queryKey: ukeys.products });
    },
  });
}
export function useDeleteAlias() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { key: string; id: string }) => call('aliases.delete', { params: { key: v.key, id: v.id } }),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: ukeys.aliases(v.key) });
      void qc.invalidateQueries({ queryKey: ukeys.products });
    },
  });
}
export function useCreateClientProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { clientId: string; body: CreateClientProductBody }) =>
      call('clientProducts.create', { params: { id: v.clientId }, body: v.body, idempotencyKey: newIdempotencyKey() }),
    onSuccess: () => refreshUsage(qc),
  });
}
export function useUpdateClientProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { clientId: string; cpId: string; body: UpdateClientProductBody }) =>
      call('clientProducts.update', { params: { id: v.clientId, cp_id: v.cpId }, body: v.body }),
    onSuccess: () => refreshUsage(qc),
  });
}
